'use client';

import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import BoardSwitcher from '@/components/common/BoardSwitcher';

import CustomerCardPanel from '@/components/common/CustomerCardPanel';
import AddCustomerModal from './AddCustomerModal';
import { mapPipelineRow, PIPELINE_COLUMNS, isValidTransition, isBackwardTransition } from '@/components/common/CustomerCardPanel';
import type { CustomerCard, StageKey, PaymentSubStatus } from '@/components/common/CustomerCardPanel';

// ─── Constants ───────────────────────────────────────────────────────────────

// Re-export COLUMNS alias for local use
const COLUMNS = PIPELINE_COLUMNS;

const STAGE_ORDER: StageKey[] = [
  'new-lead', 'appointment-scheduled', 'working-lead', 'unable-to-contact',
  'insurance-pending', 'adjuster-meeting', 'pending-ins-approval', 'overturn',
  'pa', 'insurance-approved', 'estimating', 'pending-review',
  'rts', 'signed-contract', 'contact-at-later-date',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(val: number): string {
  if (!val) return 'TBD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function getDaysInColumn(columnEnteredAt: string): number {
  const entered = new Date(columnEnteredAt).getTime();
  const diffMs = Date.now() - entered;
  return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1);
}

function getDaysColor(days: number): { bg: string; text: string } {
  if (days <= 3) return { bg: '#DCFCE7', text: '#15803D' };
  if (days <= 7) return { bg: '#FEF9C3', text: '#A16207' };
  return { bg: '#FEE2E2', text: '#B91C1C' };
}

// Formats a pure DATE ('YYYY-MM-DD') as "Mon, Jul 20". Parses the parts directly
// so the day never shifts due to UTC-midnight timezone conversion.
function formatApptDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

// "Inspection" / "Install" / fallback label from the appointment type.
function apptTypeLabel(type: string | null): string {
  if (!type) return 'Appointment';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

// ─── Payment Sub-Status Chip ─────────────────────────────────────────────────

function PaymentSubStatusChip({ subStatus }: { subStatus: PaymentSubStatus }) {
  if (!subStatus) return null;
  const config = {
    financing: { label: 'Financing', bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
    escrow:    { label: 'Escrow',    bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
    ach:       { label: 'ACH',       bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
  }[subStatus];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border"
      style={{ background: config.bg, color: config.text, borderColor: config.border }}
    >
      {config.label}
    </span>
  );
}

// ─── Soft Warning Modal ───────────────────────────────────────────────────────

interface SoftWarningModalProps {
  card: CustomerCard;
  fromStage: StageKey;
  toStage: StageKey;
  onConfirm: () => void;
  onCancel: () => void;
}

function SoftWarningModal({ card, fromStage, toStage, onConfirm, onCancel }: SoftWarningModalProps) {
  const fromCol = COLUMNS.find(c => c.id === fromStage);
  const toCol = COLUMNS.find(c => c.id === toStage);
  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 p-6 border-b border-gray-100">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 9v4M12 17h.01" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900">Move Card Backward?</h3>
            <p className="text-sm text-gray-500 mt-1">
              You're moving <span className="font-semibold text-gray-700">{card.customerFirstName} {card.customerLastName}</span> from{' '}
              <span className="font-semibold" style={{ color: fromCol?.color }}>{fromCol?.title}</span>{' '}
              back to{' '}
              <span className="font-semibold" style={{ color: toCol?.color }}>{toCol?.title}</span>.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3 border border-amber-200">
              ⚠️ Backward moves may indicate a correction or error. This action will be reflected in the pipeline immediately.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 transition-colors"
          >
            Yes, Move Backward
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Customer Card ────────────────────────────────────────────────────────────

interface CustomerCardProps {
  card: CustomerCard;
  index: number;
  colColor: string;
  onCardClick: (card: CustomerCard) => void;
}

const CustomerKanbanCard = ({ card, index, colColor, onCardClick }: CustomerCardProps) => {
  const initials = `${card.customerFirstName.charAt(0)}${card.customerLastName.charAt(0)}`.toUpperCase();
  const days = getDaysInColumn(card.columnEnteredAt);
  const { bg: daysBg, text: daysText } = getDaysColor(days);

  return (
    <Draggable draggableId={card.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onCardClick(card)}
          className={`bg-white rounded-xl p-3.5 mb-3 cursor-pointer select-none transition-all duration-150 ${
            snapshot.isDragging
              ? 'shadow-2xl ring-2 rotate-1 scale-[1.02]': 'shadow-sm hover:shadow-md'
          }`}
          style={{
            ...provided.draggableProps.style,
            borderRadius: '12px',
            ...(snapshot.isDragging ? { ringColor: colColor } : {}),
          }}
        >
          {/* Card Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
                style={{ background: `linear-gradient(135deg, ${colColor} 0%, #1C2E5E 100%)` }}
              >
                {initials}
              </div>
              <div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: '#EFF9FE', color: '#E89A3C' }}
                  >
                    {card.jobId}
                  </span>
                </div>
                <h3 className="text-sm font-bold mt-0.5 leading-tight" style={{ color: '#1C2E5E' }}>
                  {card.customerFirstName} {card.customerLastName}
                </h3>
              </div>
            </div>
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-full flex-shrink-0"
              style={{ background: daysBg }}
              title={`${days} day${days !== 1 ? 's' : ''} in this column`}
            >
              <Icon name="ClockIcon" size={10} style={{ color: daysText }} />
              <span className="text-[10px] font-bold leading-none" style={{ color: daysText }}>
                {days}d
              </span>
            </div>
          </div>

          {/* Scheduled inspection/install date + time window — prominent near the top */}
          {card.appointmentDate && (
            <div
              className="flex items-center gap-1.5 mb-2 px-2 py-1.5 rounded-lg"
              style={{ background: 'rgba(99,102,241,0.08)' }}
            >
              <span className="text-[13px] leading-none">📅</span>
              <span className="text-[11px] font-bold leading-tight" style={{ color: '#4F46E5' }}>
                {apptTypeLabel(card.appointmentType)}: {formatApptDate(card.appointmentDate)}
                {card.appointmentWindow ? ` · ${card.appointmentWindow}` : ''}
              </span>
            </div>
          )}

          <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-1.5">
            <Icon name="MapPinIcon" size={11} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">
              {card.address ? `${card.address}, ${card.city}, ${card.state}` : `${card.city}, ${card.state}`}
            </span>
          </p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-1.5">
            <Icon name="EnvelopeIcon" size={11} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{card.email || '—'}</span>
          </p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-2">
            <Icon name="PhoneIcon" size={11} className="text-gray-400 flex-shrink-0" />
            <span>{card.phone || '—'}</span>
          </p>

          <div className="grid grid-cols-3 gap-1.5 py-2 border-t border-gray-50">
            <div className="flex flex-col items-center bg-gray-50 rounded-lg px-1.5 py-1.5">
              <Icon name="HomeIcon" size={11} className="text-gray-400 mb-0.5" />
              <span className="text-[10px] font-bold text-gray-700 leading-tight">
                {card.totalSquares > 0 ? `${card.totalSquares}` : '—'}
              </span>
              <span className="text-[9px] text-gray-400 leading-tight">Squares</span>
            </div>
            <div className="flex flex-col items-center bg-indigo-50 rounded-lg px-1.5 py-1.5">
              <Icon name="CalculatorIcon" size={11} className="text-indigo-400 mb-0.5" />
              <span className="text-[10px] font-bold text-indigo-700 leading-tight">
                {card.instantEstimate > 0 ? formatCurrency(card.instantEstimate) : '—'}
              </span>
              <span className="text-[9px] text-indigo-400 leading-tight">Est. Price</span>
            </div>
            <div className="flex flex-col items-center bg-green-50 rounded-lg px-1.5 py-1.5">
              <Icon name="BanknotesIcon" size={11} className="text-green-500 mb-0.5" />
              <span className="text-[10px] font-bold text-green-700 leading-tight">
                {card.contractValue > 0 ? formatCurrency(card.contractValue) : '—'}
              </span>
              <span className="text-[9px] text-green-500 leading-tight">Contract</span>
            </div>
          </div>

          {/* Web-lead details: Material / Color / $ per square */}
          <div className="grid grid-cols-3 gap-1.5 pt-2 mt-1 border-t border-gray-50 text-center">
            <div>
              <p className="text-[9px] text-gray-400 leading-tight">Material</p>
              <p className="text-[10px] font-semibold text-gray-700 leading-tight truncate" title={card.material ?? ''}>
                {card.material || '—'}
              </p>
            </div>
            <div>
              <p className="text-[9px] text-gray-400 leading-tight">Color</p>
              <p className="text-[10px] font-semibold text-gray-700 leading-tight truncate" title={card.shingleColor ?? ''}>
                {card.shingleColor || '—'}
              </p>
            </div>
            <div>
              <p className="text-[9px] text-gray-400 leading-tight">$/sq</p>
              <p className="text-[10px] font-semibold text-gray-700 leading-tight">
                {card.costPerSquare != null
                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(card.costPerSquare)
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CustomerDashboardInteractive() {
  const supabase = createClient();

  const [cards, setCards] = useState<CustomerCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState<CustomerCard | null>(null);

  // Soft warning state for drag-and-drop backward moves
  const [pendingDrag, setPendingDrag] = useState<{ draggableId: string; newStage: StageKey; card: CustomerCard } | null>(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);

  useEffect(() => {
    loadCards();

    const channel = supabase
      .channel('customer_pipeline_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customer_pipeline' }, payload => {
        if (payload.eventType === 'INSERT') {
          // Skip jobs already handed off to production.
          if (payload.new.production_started) return;
          setCards(prev => [...prev, mapPipelineRow(payload.new)]);
        } else if (payload.eventType === 'UPDATE') {
          if (payload.new.production_started) {
            // Moved past Incoming Approved on Production → drop off the Sales board.
            setCards(prev => prev.filter(c => c.id !== payload.new.id));
            setSelectedCard(prev => prev?.id === payload.new.id ? null : prev);
          } else {
            setCards(prev => {
              const exists = prev.some(c => c.id === payload.new.id);
              // Un-hidden (moved back to Incoming) → re-add if missing.
              return exists
                ? prev.map(c => c.id === payload.new.id ? mapPipelineRow(payload.new) : c)
                : [...prev, mapPipelineRow(payload.new)];
            });
            setSelectedCard(prev => prev?.id === payload.new.id ? mapPipelineRow(payload.new) : prev);
          }
        } else if (payload.eventType === 'DELETE') {
          setCards(prev => prev.filter(c => c.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const loadCards = async () => {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('customer_pipeline')
        .select('*')
        .order('created_at', { ascending: true });

      if (err) throw err;
      // Hide jobs that have advanced past Incoming Approved on the production board.
      setCards((data ?? []).filter((r: any) => !r.production_started).map(mapPipelineRow));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load customer pipeline');
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStage = destination.droppableId as StageKey;
    const card = cards.find(c => c.id === draggableId);
    if (!card) return;

    // If backward move, show soft warning instead of hard blocking
    if (isBackwardTransition(card.stage, newStage)) {
      setPendingDrag({ draggableId, newStage, card });
      return;
    }

    await applyStageMove(draggableId, newStage, card);
  };

  const applyStageMove = async (draggableId: string, newStage: StageKey, card: CustomerCard) => {
    const now = new Date().toISOString();
    const isNewColumn = newStage !== card.stage;

    setCards(prev => prev.map(c =>
      c.id === draggableId
        ? { ...c, stage: newStage, ...(isNewColumn ? { columnEnteredAt: now } : {}) }
        : c
    ));
    if (selectedCard?.id === draggableId) {
      setSelectedCard(prev => prev
        ? { ...prev, stage: newStage, ...(isNewColumn ? { columnEnteredAt: now } : {}) }
        : prev
      );
    }

    try {
      const updatePayload: Record<string, string> = { stage: newStage, updated_at: now };
      if (isNewColumn) updatePayload.column_entered_at = now;
      const { error: err } = await supabase.from('customer_pipeline').update(updatePayload).eq('id', draggableId);
      if (err) throw err;
    } catch {
      setCards(prev => prev.map(c => c.id === draggableId ? card : c));
    }
  };

  const handleStageChange = async (cardId: string, stage: StageKey) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;
    if (!isValidTransition(card.stage, stage)) return;

    const now = new Date().toISOString();
    const isNewColumn = stage !== card.stage;

    setCards(prev => prev.map(c =>
      c.id === cardId
        ? { ...c, stage, ...(isNewColumn ? { columnEnteredAt: now } : {}) }
        : c
    ));
    setSelectedCard(prev =>
      prev?.id === cardId
        ? { ...prev, stage, ...(isNewColumn ? { columnEnteredAt: now } : {}) }
        : prev
    );

    try {
      const updatePayload: Record<string, string> = { stage, updated_at: now };
      if (isNewColumn) updatePayload.column_entered_at = now;
      const { error: err } = await supabase.from('customer_pipeline').update(updatePayload).eq('id', cardId);
      if (err) throw err;
    } catch {
      setCards(prev => prev.map(c => c.id === cardId ? card : c));
    }
  };

  const filteredCards = cards.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.jobId.toLowerCase().includes(q) ||
      c.customerFirstName.toLowerCase().includes(q) ||
      c.customerLastName.toLowerCase().includes(q) ||
      c.city.toLowerCase().includes(q) ||
      c.state.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  });

  const getColumnCards = (stageId: StageKey) =>
    filteredCards.filter(c => c.stage === stageId);

  return (
    <div className="flex flex-col" style={{ background: '#F8F9FA', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>

      {/* Soft Warning Modal for backward drag */}
      {pendingDrag && (
        <SoftWarningModal
          card={pendingDrag.card}
          fromStage={pendingDrag.card.stage}
          toStage={pendingDrag.newStage}
          onConfirm={async () => {
            const { draggableId, newStage, card } = pendingDrag;
            setPendingDrag(null);
            await applyStageMove(draggableId, newStage, card);
          }}
          onCancel={() => setPendingDrag(null)}
        />
      )}

      {/* Sticky Header */}
      <header
        className="flex-shrink-0 flex items-center justify-between px-6 py-3.5 bg-white border-b border-gray-100 shadow-sm z-30"
        style={{ position: 'sticky', top: 0 }}
      >
        <div>
          <BoardSwitcher current="sales" />
          <p className="text-xs text-gray-400 mt-1 ml-1">{cards.length} customers across pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden sm:block">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:bg-white w-52 transition-all"
            />
          </div>
          <button
            onClick={() => setShowAddCustomer(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: '#1C2E5E' }}
          >
            <Icon name="PlusIcon" size={16} className="text-white" variant="solid" />
            <span className="hidden sm:inline">New Customer</span>
          </button>
          <button
            onClick={loadCards}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: '#E89A3C' }}
          >
            <Icon name="ArrowPathIcon" size={16} className="text-white" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {/* New Customer modal */}
      {showAddCustomer && (
        <AddCustomerModal
          onClose={() => setShowAddCustomer(false)}
          onCreated={loadCards}
        />
      )}

      {/* Error Banner */}
      {error && (
        <div className="flex-shrink-0 mx-6 mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2">
          <Icon name="ExclamationTriangleIcon" size={16} className="text-red-500 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <Icon name="XMarkIcon" size={14} />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-4 border-[#E89A3C] border-t-transparent animate-spin" />
            <p className="text-sm text-gray-500">Loading customer pipeline...</p>
          </div>
        </div>
      ) : (
        /* Kanban Board */
        <div style={{ flex: 1, overflowX: 'scroll', overflowY: 'auto', minHeight: 0 }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <div style={{ display: 'flex', gap: '16px', padding: '20px 24px', height: '100%', minWidth: 'max-content', boxSizing: 'border-box' }}>
              {COLUMNS.map(col => {
                const colCards = getColumnCards(col.id);
                const colTotal = colCards.reduce((s, c) => s + (c.contractValue || 0), 0);
                return (
                  <div
                    key={col.id}
                    style={{
                      background: '#F3F4F6',
                      width: '280px',
                      minWidth: '280px',
                      flexShrink: 0,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      className="px-4 py-3.5 flex items-center justify-between flex-shrink-0"
                      style={{ background: col.color }}
                    >
                      <div>
                        <h2 className="text-white font-bold text-sm leading-tight">{col.title}</h2>
                        <p className="text-white/60 text-[11px] mt-0.5">{col.subtitle}</p>
                      </div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 bg-white/20 text-white">
                        {colCards.length}
                      </span>
                    </div>

                    {/* Approved Estimate Total roll-up */}
                    <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100 flex-shrink-0">
                      <span className="text-[11px] font-medium text-gray-500">Approved Estimate Total</span>
                      <span className="text-[13px] font-bold" style={{ color: '#1C2E5E' }}>
                        {formatCurrency(colTotal)}
                      </span>
                    </div>

                    <Droppable droppableId={col.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 overflow-y-auto px-3 pt-3 pb-2 transition-colors min-h-[200px] ${
                            snapshot.isDraggingOver ? 'bg-blue-50/60' : ''
                          }`}
                        >
                          {colCards.length === 0 && !snapshot.isDraggingOver && (
                            <div className="flex flex-col items-center justify-center py-10 text-center">
                              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mb-2">
                                <Icon name="UserIcon" size={20} className="text-gray-400" />
                              </div>
                              <p className="text-sm text-gray-400 font-medium">No customers</p>
                              <p className="text-[11px] text-gray-300 mt-1">Drag cards to add</p>
                            </div>
                          )}
                          {colCards.map((card, index) => (
                            <CustomerKanbanCard
                              key={card.id}
                              card={card}
                              index={index}
                              colColor={col.color}
                              onCardClick={setSelectedCard}
                            />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>
      )}

      {/* Unified Customer Card Panel */}
      {selectedCard && (
        <CustomerCardPanel
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onStageChange={handleStageChange}
        />
      )}
    </div>
  );
}
